import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import IconSelect from '../Form/IconSelect';
import type { Category } from '@/types/common';

interface CategoryModalProps {
  category?: Category;
  availableIcons: Array<{ name: string; icon: string; }>;
  onClose: () => void;
  onSubmit: (data: { name: string; icon: string; }) => Promise<void>;
}

export default function CategoryModal({ category, availableIcons, onClose, onSubmit }: CategoryModalProps) {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    icon: category?.icon || 'fa-utensils'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting category:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg 
                bg-white dark:bg-dark-bg p-6 shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 dark:text-dark-text-primary mb-4"
                >
                  {category ? 'Edit Category' : 'New Category'}
                </Dialog.Title>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
                    >
                      Category Name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="block w-full rounded-lg border border-gray-300 dark:border-dark-border shadow-sm 
                          focus:ring-1 focus:ring-primary-500 focus:border-primary-500 
                          dark:bg-dark-bg-light dark:text-dark-text-primary
                          py-2.5 pl-3 pr-10 text-sm transition-colors duration-200"
                        placeholder="Enter category name"
                      />
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <i className="fas fa-pencil-alt text-gray-400 dark:text-dark-text-secondary text-xs" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <IconSelect
                      label="Icon"
                      value={formData.icon}
                      options={availableIcons}
                      onChange={(icon) => setFormData(prev => ({ ...prev, icon }))}
                    />
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="btn btn-outline btn-outline-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!formData.name || isSubmitting}
                      className="btn btn-primary"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="fas fa-circle-notch fa-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        category ? 'Save Changes' : 'Create Category'
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
} 